import React, { useState, useEffect } from 'react';
import Logo from '../../components/common/Logo';
import { User, Save} from 'lucide-react';
import DoctorSidebar from '../../components/doctor/DoctorSidebar';
import DoctorService from '../../services/doctor.service';
import { UPLOAD_PREFIX } from '../../config/appConfig';
import ChangePasswordBox from '../../components/doctor/ChangePasswordBox';

const DoctorSettingsPage = () => {
  const [profileData, setProfileData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    biography: "",
    professional_story: "",
    default_price_hour: "",
    profile_picture: null,
    specialization: "",
    office_address: "",
    therapist_id: null,
    tags: []
  });

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const data = await DoctorService.getProfile();
      setProfileData({
        first_name: data.user.first_name,
        last_name: data.user.last_name,
        email: data.user.email,
        phone: data.user.phone,
        biography: data.biography || '',
        professional_story: data.professional_story || '',
        default_price_hour: data.default_price_hour || '',
        profile_picture: data.profile_picture,
        specialization: data.specialization || '',
        office_address: data.office_address || '',
        therapist_id: data.id,
        tags: data.tags || [],
      });
    } catch (error) {
      console.error('[DEBUG] Error loading profile:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    try {
      await DoctorService.updateProfile({
        biography: profileData.biography,
        professional_story: profileData.professional_story,
        default_price_hour: profileData.default_price_hour,
        specialization: profileData.specialization,
        office_address: profileData.office_address,
        phone: profileData.phone 
      });
      console.log('Profile updated successfully');
    } catch (error) {
      console.error('[DEBUG] Error updating profile:', error);
    }
  };

  return (
    <div className="flex min-h-screen">
      <div className="w-64 bg-white shadow-md">
        <div className="p-4">
          <Logo />
        </div>
        <DoctorSidebar />
      </div>

      <div className="flex-1 bg-[#FAF4EB] p-8">
        <header className="mb-8">
          <h1 className="text-2xl font-bold">Impostazioni Profilo</h1>
          <p className="text-gray-600">Gestisci le tue informazioni professionali</p>
        </header>

        <div className="max-w-4xl">
          {/* Foto Profilo */}
          <div className="bg-white p-6 rounded-lg shadow-md mb-6">
              <h2 className="text-lg font-semibold mb-4">Foto Profilo</h2>
              <div className="flex items-center">
                {profileData.profile_picture ? (
                  <img
                    src={`${UPLOAD_PREFIX}/storage/${profileData.profile_picture}`}
                    alt="Profile"
                    className="w-24 h-24 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-24 h-24 bg-purple-100 rounded-full flex items-center justify-center">
                    <User className="w-12 h-12 text-purple-600" />
                  </div>
                )}
              </div>
            </div>

          <ChangePasswordBox />

          {/* Informazioni Base */}
          <div className="bg-white p-6 rounded-lg shadow-md mb-6">
            <h2 className="text-lg font-semibold mb-4">Informazioni Personali</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nome Completo</label>
                <input
                  type="text"
                  value={`${profileData.first_name} ${profileData.last_name}`}
                  disabled
                  className="w-full p-2 border border-gray-300 rounded-md bg-gray-50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  value={profileData.email}
                  disabled
                  className="w-full p-2 border border-gray-300 rounded-md bg-gray-50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Telefono</label>
                <input
                  type="tel"
                  value={profileData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Indirizzo Studio</label>
                <input
                  type="text"
                  value={profileData.office_address}
                  onChange={(e) => handleInputChange('office_address', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Bio e Specializzazioni */}
          <div className="bg-white p-6 rounded-lg shadow-md mb-6">
            <h2 className="text-lg font-semibold mb-4">Profilo Professionale</h2>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Biografia</label>
                <textarea
                  value={profileData.biography}
                  onChange={(e) => handleInputChange('biography', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  rows={4}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Storia Professionale</label>
                <textarea
                  value={profileData.professional_story}
                  onChange={(e) => handleInputChange('professional_story', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  rows={3}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Specializzazione</label>
                <input
                  type="text"
                  value={profileData.specialization}
                  onChange={(e) => handleInputChange('specialization', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Pulsante Salva */}
          <div className="flex justify-end">
            <button
              onClick={handleSave}
              className="px-6 py-3 bg-purple-600 text-white rounded-md hover:bg-purple-700 flex items-center"
            >
              <Save className="w-4 h-4 mr-2" />
              Salva Modifiche
            </button>
          </div>

          {/* Box dei Tag
          {profileData.therapist_id && (
            <div className="mt-6">
            <DoctorTagsBox 
                  therapistId={profileData.therapist_id} 
                  initialTags={profileData.tags}
                />
            </div>
          )}
          */}
        </div>
         {/*
        <DoctorServices therapistId={profileData.therapist_id} />
         */}
        </div>
    </div>
  );
};

export default DoctorSettingsPage;